'use client';

import { useEffect, useCallback, useState, useRef, useContext } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { FlagshipContext } from '@flagship.io/react-sdk';
import { useRequestQueue } from '@web/queue/context';
import { ApiResult, useService } from '../api';
import { useCampaign } from '../campaign';
import { useExperiment } from '../experiments/useExperiment';
import { CHECKOUT_CX_CTAS } from '../experiments/constants';
import { SAMPLER_OFFER_CODE_COOKIE } from '../offer/samplerOffer';
import { ShopifyCartService } from './services';
import { getABTestFlags } from './helpers';

const emptyApplyResult = { applied: null, cart: null, cleared: false };

export const getUrlOfferCode = () => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('offer_code');
};

export const resolveRouterOfferCode = offerCode => {
  if (Array.isArray(offerCode)) return offerCode[0] || null;
  return offerCode || null;
};

export const getSamplerOfferCodeToApply = () =>
  getUrlOfferCode() || Cookies.get(SAMPLER_OFFER_CODE_COOKIE) || null;

export const cartCouponMatchesOffer = (cartCouponCode, offerCode) =>
  Boolean(
    cartCouponCode &&
      offerCode &&
      cartCouponCode.toLowerCase() === offerCode.toLowerCase(),
  );

export const resolveSamplerFlagshipOfferCode = (
  cartCouponCode,
  intentOfferCode,
) => {
  if (intentOfferCode) {
    return cartCouponMatchesOffer(cartCouponCode, intentOfferCode)
      ? cartCouponCode.toLowerCase()
      : '';
  }

  return cartCouponCode ? cartCouponCode.toLowerCase() : '';
};

export const shouldReleaseSamplerOfferFlags = ({
  shouldCreateCart,
  hasCheckedPath,
  cartError,
  cartInitFailed,
}) =>
  Boolean(cartError) ||
  Boolean(cartInitFailed) ||
  (Boolean(hasCheckedPath) && !shouldCreateCart);

const getCartPayload = response => response?.data ?? response ?? null;

const getCartCoupon = cart => cart?.coupon_code?.[0] ?? '';

const persistUrlOfferCodeCookie = () => {
  const urlCode = getUrlOfferCode();
  if (urlCode) {
    Cookies.set(SAMPLER_OFFER_CODE_COOKIE, urlCode, { path: '/' });
  }
};

const clearSamplerCartPromo = async (service, cartId) => {
  try {
    const response = await service.addPromo(cartId, null);
    return {
      applied: null,
      cart: getCartPayload(response),
      cleared: true,
    };
  } catch {
    return { applied: null, cart: null, cleared: true };
  }
};

export const applySamplerOfferCode = async (service, cartId) => {
  if (!cartId) return emptyApplyResult;

  persistUrlOfferCodeCookie();

  const offerCode = getSamplerOfferCodeToApply();
  if (!offerCode) return emptyApplyResult;

  try {
    await service.validatePromo(offerCode);
  } catch {
    // Latest URL/cookie is the intent; drop the previous sampler coupon so
    // intro/crunching do not keep showing the last applicable code.
    return clearSamplerCartPromo(service, cartId);
  }

  try {
    const response = await service.addPromo(cartId, offerCode);
    const cart = getCartPayload(response);
    if (!cartCouponMatchesOffer(getCartCoupon(cart), offerCode)) {
      return clearSamplerCartPromo(service, cartId);
    }

    return {
      applied: offerCode,
      cart,
      cleared: false,
    };
  } catch {
    return clearSamplerCartPromo(service, cartId);
  }
};

export const useSamplerCart = ({
  shouldCreateCart = true,
  hasCheckedPath = false,
} = {}) => {
  const router = useRouter();
  const service = useService(ShopifyCartService);
  const { mutate } = useSWRConfig();
  const { queueRequest } = useRequestQueue();
  const { campaignId } = useCampaign();
  const { value: checkoutCxCtas } = useExperiment(CHECKOUT_CX_CTAS, 'control');
  const { state: flagshipState } = useContext(FlagshipContext);
  const urlOfferCode = router.isReady
    ? resolveRouterOfferCode(router.query.offer_code)
    : null;
  const [cartId, setCartId] = useState(null);
  const [isFetchingOfferFlags, setIsFetchingOfferFlags] = useState(true);
  const [cartInitFailed, setCartInitFailed] = useState(false);
  const creatingRef = useRef(null); //* Updated to hold promise instead of boolean
  const cartIdRef = useRef(cartId);
  const flagsFetchedForRef = useRef(null);

  // Keep cartIdRef.current updated with the latest cartId
  cartIdRef.current = cartId;

  const updateCartId = useCallback(
    newId => {
      if (newId && newId !== cartId) {
        setCartId(newId);
        Cookies.set('samplerCartId', newId, { path: '/', expires: 30 });
      }
    },
    [cartId],
  );

  const createCart = useCallback(async () => {
    if (cartIdRef.current) return { cart_id: cartIdRef.current };

    //* If a creation is already in progress, return the existing promise
    if (creatingRef.current) {
      return creatingRef.current;
    }

    creatingRef.current = (async () => {
      try {
        const response = await service.createCart();

        if (response?.data?.cart_id) {
          setCartInitFailed(false);
          await applySamplerOfferCode(service, response.data.cart_id);
          updateCartId(response.data.cart_id);
          return response.data;
        }
        setCartInitFailed(true);
        setIsFetchingOfferFlags(false);
        return response?.data || null;
      } catch (error) {
        creatingRef.current = null;
        setCartInitFailed(true);
        setIsFetchingOfferFlags(false);
        return null;
      }
    })();

    return creatingRef.current;
  }, [service, updateCartId]);

  const {
    data: cartData,
    error: cartError,
    isLoading: isShopifyCartLoading,
    mutate: refreshCart,
  } = useSWR(cartId ? `/api/v4/onboarding/${cartId}/` : null, null, {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    dedupingInterval: 10000,
    onSuccess: async data => {
      const attributes = {
        order_type: 'onboarding',
        sample_box_order: 'true',
        cart_id: data.cart_id,
        ab_tests: getABTestFlags({ checkoutCxCtas }),
      };
      queueRequest(() => updateCartAttributes(attributes, data.cart_id));
    },
    onError: async error => {
      if (error.response?.status === 404) {
        Cookies.remove('samplerCartId', { path: '/' });
        setCartId(null);
        if (shouldCreateCart) {
          const created = await createCart();
          if (!created?.cart_id) {
            setCartInitFailed(true);
            setIsFetchingOfferFlags(false);
          }
        }
      }
    },
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (retryCount >= 1) return;
      setTimeout(() => revalidate({ retryCount }), 1000);
    },
  });

  const addPetData = useCallback(
    async (petData = {}) => {
      let currentCartId = cartIdRef.current; // potential race condition handling with ref

      // If no cartId, try to create one
      if (!currentCartId) {
        const newCart = await createCart();
        currentCartId = newCart?.cart_id;
      }

      if (!currentCartId) {
        return null;
      }

      try {
        const response = await service.addPetData(currentCartId, {
          ...petData,
        });

        if (!currentCartId) {
          return null;
        }

        await refreshCart(response.data, false);
        return response?.data;
      } catch (error) {
        throw error;
      }
    },
    [service, refreshCart, createCart],
  );

  const deletePetFromCart = useCallback(
    async petId => {
      if (!cartId) return null;

      const response = await service.removePet(cartId, petId);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, refreshCart],
  );

  const addPromo = useCallback(
    async promoCode => {
      if (!cartId || cartError) return null;

      const response = await service.addPromo(cartId, promoCode);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, cartError, refreshCart],
  );

  const upsertMealPlan = useCallback(
    async (petId, planData = {}) => {
      if (!cartId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.upsertMealPlan(cartId, petId, planData);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const attachExtras = useCallback(
    async (petId, extrasData = []) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachExtras(cartId, petId, extrasData);
        refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const updateCartAttributes = useCallback(
    async (attributes = {}, idOverride = null) => {
      const id = idOverride || cartId;

      if (!id) {
        // eslint-disable-next-line no-console
        console.warn('updateCartAttributes: No Cart ID available');
        return null;
      }

      return ApiResult.callAsync(async () => {
        const response = await service.updateAttributes(id, attributes);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  useEffect(() => {
    //* There is a bug where a cart is created twice. This doesn't fix it directly...
    //* but i found that the createCart call being awaited was better for adding the attribute following.
    //TODO: Refactor to avoid double calls altogether. (...Once i find where it is happening of course)
    const initCart = async () => {
      if (!shouldCreateCart) return;

      setIsFetchingOfferFlags(true);
      const savedCartId = Cookies.get('samplerCartId');

      if (savedCartId) {
        const result = await applySamplerOfferCode(service, savedCartId);
        if (result?.cart) {
          await mutate(
            `/api/v4/onboarding/${savedCartId}/`,
            result.cart,
            false,
          );
        }
        if (cartIdRef.current === savedCartId && result?.cart) {
          await refreshCart(result.cart, false);
        }
        setCartId(savedCartId);
        setCartInitFailed(false);
      } else {
        try {
          const created = await createCart();
          if (!created?.cart_id) {
            setCartInitFailed(true);
            setIsFetchingOfferFlags(false);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize cart:', error);
          setCartInitFailed(true);
          setIsFetchingOfferFlags(false);
        }
      }
    };

    initCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreateCart, urlOfferCode]);

  // Update cart attributes with campaign_id when cart is first created
  useEffect(() => {
    if (!cartId || !campaignId) return;
    const updateCampaign = async campaignId => {
      queueRequest(() =>
        updateCartAttributes({
          campaign_id: campaignId,
        }),
      );
    };
    updateCampaign(campaignId);
  }, [cartId, updateCartAttributes, queueRequest, campaignId]);

  const getCouponCode = useCallback(() => {
    if (!cartData?.coupon_code?.length) return null;
    return cartData.coupon_code[0];
  }, [cartData]);

  const updatedABTestFlags = getABTestFlags({ checkoutCxCtas });
  const hasCartData = Boolean(cartData);

  //* Keep checkoutCxCtas in sync
  useEffect(() => {
    if (
      !cartId ||
      !hasCartData ||
      shouldReleaseSamplerOfferFlags({
        shouldCreateCart,
        hasCheckedPath,
        cartError,
        cartInitFailed,
      })
    ) {
      return;
    }

    queueRequest(() =>
      updateCartAttributes({
        ab_tests: updatedABTestFlags,
      }),
    );
  }, [
    cartId,
    hasCartData,
    cartError,
    cartInitFailed,
    hasCheckedPath,
    shouldCreateCart,
    updatedABTestFlags,
    queueRequest,
    updateCartAttributes,
  ]);

  useEffect(() => {
    if (!shouldCreateCart && hasCheckedPath && !cartId) {
      setIsFetchingOfferFlags(false);
    }
  }, [shouldCreateCart, hasCheckedPath, cartId]);

  // Sync Flagship from the sampler cart coupon only while the sampler flow is
  // active. SamplerCartProvider is global on the pages router and would
  // otherwise overwrite starter flags on /onboarding.
  useEffect(() => {
    if (
      shouldReleaseSamplerOfferFlags({
        shouldCreateCart,
        hasCheckedPath,
        cartError,
        cartInitFailed,
      })
    ) {
      flagsFetchedForRef.current = null;
      setIsFetchingOfferFlags(false);
      return;
    }

    if (!cartData || !cartId) {
      return;
    }

    const cartCouponCode = cartData?.coupon_code?.[0] ?? '';
    const flagOfferCode = resolveSamplerFlagshipOfferCode(
      cartCouponCode,
      getSamplerOfferCodeToApply(),
    );
    const visitor = flagshipState?.visitor;

    if (visitor && flagsFetchedForRef.current !== flagOfferCode) {
      flagsFetchedForRef.current = flagOfferCode;
      setIsFetchingOfferFlags(true);
      visitor.updateContext({ offerCode: flagOfferCode });
      visitor.fetchFlags().finally(() => setIsFetchingOfferFlags(false));
    } else {
      setIsFetchingOfferFlags(false);
    }
  }, [
    cartData,
    cartId,
    cartError,
    cartInitFailed,
    flagshipState?.visitor,
    hasCheckedPath,
    shouldCreateCart,
  ]);

  const isLoading =
    !hasCheckedPath ||
    isShopifyCartLoading ||
    (shouldCreateCart && !cartData && !cartError && !cartInitFailed);

  return {
    cartId,
    cart: cartData || {},
    error: cartError,
    isLoading,
    isFetchingOfferFlags,
    createCart,
    addPetData,
    deletePetFromCart,
    addPromo,
    upsertMealPlan,
    attachExtras,
    refreshCart,
    couponCode: getCouponCode(),
  };
};
